import styled, { css } from 'styled-components';
import type { DefaultTheme } from 'styled-components';
import { fleetStatusLabel, fleetStatusTone, type FleetTone } from '../model';
import { fleetType } from './tokens';

function dotColor(theme: DefaultTheme, tone: FleetTone): string {
  switch (tone) {
    case 'success':
      return theme.colors.status.success;
    case 'info':
      return theme.colors.status.info;
    case 'danger':
      return theme.colors.status.danger;
    case 'warning':
      return theme.colors.status.warning;
    case 'neutral':
      return theme.colors.text.secondary;
    case 'muted':
      return 'transparent';
  }
}

/**
 * Метка статуса или признака. Цвет — только у точки и подложки: текст всегда
 * основного цвета, поэтому метка читается и без цвета (оранжевый и зелёный
 * темы мелким текстом AA не проходят). «В архиве» — пустое кольцо.
 */
export const FleetChip = styled.span<{ $tone: FleetTone }>`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1.5)};
  min-height: 22px;
  padding: ${({ theme }) => `1px ${theme.spacing(2)}`};
  border-radius: ${({ theme }) => theme.radius.pill};
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: ${fleetType.small};
  font-weight: 500;
  line-height: 1.3;
  white-space: nowrap;

  &::before {
    content: '';
    flex: none;
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: ${({ theme, $tone }) => dotColor(theme, $tone)};
  }

  ${({ $tone, theme }) =>
    $tone === 'warning' &&
    css`
      background: ${theme.colors.status.warning}1F;
      border-color: ${theme.colors.status.warning}73;
    `}

  ${({ $tone, theme }) =>
    $tone === 'muted' &&
    css`
      color: ${theme.colors.text.secondary};
      &::before {
        box-shadow: inset 0 0 0 1.5px ${theme.colors.text.secondary};
      }
    `}
`;

export function FleetStatusChip({ status }: { status: string }) {
  return <FleetChip $tone={fleetStatusTone(status)}>{fleetStatusLabel(status)}</FleetChip>;
}

/** «ПРЦ устарела» — исходные данные расчёта изменились после утверждения цены. */
export function PrcStaleChip() {
  return (
    <FleetChip
      $tone="warning"
      title="Исходные данные расчёта изменились после утверждения цены машино-часа — модель попадёт в список на пересчёт"
    >
      ПРЦ устарела
    </FleetChip>
  );
}

/** Маленькая метка «ПРЦ» у поля, от которого зависит цена машино-часа. */
export const PrcMark = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0 ${({ theme }) => theme.spacing(1.5)};
  border-radius: ${({ theme }) => theme.radius.xs};
  border: 1px solid ${({ theme }) => theme.colors.status.warning}80;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${fleetType.micro};
  font-weight: 600;
  letter-spacing: 0.04em;
  line-height: 16px;
  white-space: nowrap;
  vertical-align: 1px;
`;
